import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Header } from '../components/Header';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Mail, Send, Trash2, CheckCircle2, XCircle, Eye, Clock, Copy, Check, Pencil, UserPlus, ShieldCheck } from 'lucide-react';
import { GuestFormsDialog } from '../components/GuestFormsDialog';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_PILLS = {
  pending:  { icon: Clock,         text: 'Pending',  cls: 'bg-[#FFFDF8] text-[#6B6B7A] border-[#E8E5DF]' },
  sent:     { icon: Mail,          text: 'Sent',     cls: 'bg-[#FF7A2E]/15 text-[#FF7A2E] border-[#FF7A2E]/25' },
  opened:   { icon: Eye,           text: 'Opened',   cls: 'bg-[#6366F1]/15 text-[#6366F1] border-[#6366F1]/25' },
  yes:      { icon: CheckCircle2,  text: 'Yes',      cls: 'bg-[#06D6A0]/15 text-[#058B69] border-[#06D6A0]/25' },
  no:       { icon: XCircle,       text: 'No',       cls: 'bg-[#FF3B6B]/15 text-[#FF3B6B] border-[#FF3B6B]/25' },
  failed:   { icon: XCircle,       text: 'Failed',   cls: 'bg-[#FF3B6B]/15 text-[#FF3B6B] border-[#FF3B6B]/25' },
};

const StatusPill = ({ status }) => {
  const p = STATUS_PILLS[status] || STATUS_PILLS.pending;
  const Icon = p.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border ${p.cls}`} data-testid={`status-${status}`}>
      <Icon className="w-3 h-3" /> {p.text}
    </span>
  );
};

const InviteDetail = () => {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [copiedToken, setCopiedToken] = useState('');
  const [formsModalOpen, setFormsModalOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({ child_name: '', age: '', party_date: '', party_time: '', location: '', message: '', photo_url: '' });
  const [savingEdit, setSavingEdit] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [addText, setAddText] = useState('');
  const [addingRecipients, setAddingRecipients] = useState(false);
  const navigate = useNavigate();

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/customer/invitations/${id}`, { withCredentials: true });
      setData(res.data);
    } catch (e) {
      if (e.response?.status === 401) navigate('/login');
      else if (e.response?.status === 404) navigate('/my-invites');
      else toast.error('Could not load invitation');
    } finally { setLoading(false); }
  };
  useEffect(() => { fetchData(); }, [id]);

  const resend = async () => {
    setSending(true);
    try {
      const res = await axios.post(`${API_URL}/api/customer/invitations/${id}/send`, {}, { withCredentials: true });
      toast.success(`Sent to ${res.data.sent} recipient(s)`);
      fetchData();
    } catch { toast.error('Could not send'); }
    finally { setSending(false); }
  };

  const removeRecipient = async (rid) => {
    if (!window.confirm('Remove this recipient?')) return;
    try {
      await axios.delete(`${API_URL}/api/customer/invitations/${id}/recipients/${rid}`, { withCredentials: true });
      fetchData();
    } catch { toast.error('Could not remove'); }
  };

  const copyLink = (token) => {
    const url = `${window.location.origin}/rsvp/${token}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopiedToken(token);
      setTimeout(() => setCopiedToken(''), 1500);
      toast.success('RSVP link copied');
    });
  };

  const openEdit = () => {
    const inv = data?.invitation || {};
    setEditForm({
      child_name: inv.child_name || '',
      age: inv.age || '',
      party_date: inv.party_date || '',
      party_time: inv.party_time || '',
      location: inv.location || '',
      message: inv.message || '',
      photo_url: inv.photo_url || '',
    });
    setEditOpen(true);
  };

  const saveEdit = async () => {
    setSavingEdit(true);
    try {
      const payload = {
        child_name: editForm.child_name,
        age: parseInt(editForm.age, 10) || 0,
        party_date: editForm.party_date,
        party_time: editForm.party_time,
        location: editForm.location,
        message: editForm.message,
        photo_url: editForm.photo_url || null,
      };
      await axios.patch(`${API_URL}/api/customer/invitations/${id}`, payload, { withCredentials: true });
      toast.success('Invitation updated');
      setEditOpen(false);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not update');
    } finally { setSavingEdit(false); }
  };

  // Parse the textarea — same logic as wizard CSV paste
  const parseAddText = (text) => {
    const out = [];
    for (const ln of (text || '').split(/\r?\n/)) {
      const s = ln.trim(); if (!s) continue;
      const parts = s.split(/[,\t]/).map((p) => p.trim());
      let name = '', email = '', phone = '';
      parts.forEach((p) => {
        if (!p) return;
        if (p.includes('@')) email = p;
        else if (/^\+?\d[\d\s\-().]{6,}$/.test(p)) phone = p;
        else if (!name) name = p;
      });
      if (email || phone) out.push({ name: name || 'Guest', email, phone });
    }
    return out;
  };

  const submitAddRecipients = async () => {
    const list = parseAddText(addText);
    if (list.length === 0) { toast.error('Add at least one recipient'); return; }
    setAddingRecipients(true);
    try {
      const res = await axios.post(`${API_URL}/api/customer/invitations/${id}/recipients`, { recipients: list }, { withCredentials: true });
      const { added, sent, auto_sent } = res.data || {};
      if (auto_sent) toast.success(`Added ${added} — invite sent to ${sent} new guest${sent === 1 ? '' : 's'}`);
      else toast.success(`Added ${added} recipient${added === 1 ? '' : 's'}`);
      setAddOpen(false);
      setAddText('');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not add recipients');
    } finally { setAddingRecipients(false); }
  };

  if (loading) return <div className="min-h-screen bg-[#FFFDF8]"><Header /><p className="container mx-auto p-10 text-[#6B6B7A]">Loading…</p></div>;
  if (!data) return null;

  const { invitation, recipients = [] } = data;
  const yes = recipients.filter((r) => r.rsvp_response === 'yes').length;
  const no = recipients.filter((r) => r.rsvp_response === 'no').length;
  const opened = recipients.filter((r) => ['opened', 'yes', 'no'].includes(r.status)).length;
  const pending = recipients.length - yes - no;
  const formsCompleted = recipients.filter((r) => r.allergy_form_completed).length;

  return (
    <div className="min-h-screen bg-[#FFFDF8]" data-testid="invite-detail-page">
      <Header />
      <div className="container mx-auto px-4 py-10 max-w-4xl">
        <Link to="/my-invites" className="text-sm text-[#6B6B7A] hover:text-[#1E1E24]">← Back to invites</Link>
        <div className="mt-3 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-3xl font-bold text-[#1E1E24]" style={{ fontFamily: 'Fredoka' }} data-testid="invite-title">
              {invitation.child_name}'s Birthday Invite
            </h1>
            <p className="text-sm text-[#6B6B7A] mt-1">📅 {invitation.party_date} · ⏰ {invitation.party_time} · 📍 {invitation.location}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button onClick={openEdit} variant="outline" className="rounded-full h-10 px-4" data-testid="edit-invite-btn">
              <Pencil className="w-4 h-4 mr-1.5" /> Edit invitation
            </Button>
            <Button onClick={() => setAddOpen(true)} variant="outline" className="rounded-full h-10 px-4 border-[#6366F1]/30 text-[#6366F1] hover:bg-[#6366F1]/10" data-testid="add-recipients-btn">
              <UserPlus className="w-4 h-4 mr-1.5" /> Add recipients
            </Button>
            <Button onClick={resend} disabled={sending} className="rounded-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white h-10 px-5" data-testid="resend-btn">
              <Send className="w-4 h-4 mr-1.5" /> {sending ? 'Sending…' : 'Send to non-responders'}
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="mt-6 grid grid-cols-2 sm:grid-cols-5 gap-3">
          <div className="bg-white rounded-2xl border border-[#E8E5DF] p-4 text-center">
            <p className="text-2xl font-bold text-[#1E1E24]">{recipients.length}</p>
            <p className="text-xs text-[#6B6B7A]">Total</p>
          </div>
          <div className="bg-white rounded-2xl border border-[#E8E5DF] p-4 text-center">
            <p className="text-2xl font-bold text-[#6366F1]">{opened}</p>
            <p className="text-xs text-[#6B6B7A]">Opened</p>
          </div>
          <div className="bg-white rounded-2xl border border-[#E8E5DF] p-4 text-center">
            <p className="text-2xl font-bold text-[#06D6A0]" data-testid="stat-yes">{yes}</p>
            <p className="text-xs text-[#6B6B7A]">Coming</p>
          </div>
          <div className="bg-white rounded-2xl border border-[#E8E5DF] p-4 text-center">
            <p className="text-2xl font-bold text-[#FF3B6B]">{no}</p>
            <p className="text-xs text-[#6B6B7A]">Declined</p>
          </div>
          <button
            onClick={() => setFormsModalOpen(true)}
            className="bg-white rounded-2xl border border-[#E8E5DF] p-4 text-center hover:border-[#06D6A0] hover:shadow-sm transition-all"
            data-testid="forms-completed-stat">
            <p className="text-2xl font-bold text-[#0F8B5C] flex items-center justify-center gap-1">
              <ShieldCheck className="w-5 h-5" />{formsCompleted}/{recipients.length}
            </p>
            <p className="text-xs text-[#6B6B7A]">Forms done</p>
          </button>
        </div>

        <GuestFormsDialog
          open={formsModalOpen}
          onOpenChange={setFormsModalOpen}
          title={`Completed forms — ${invitation.child_name || 'party'}`}
          loader={async () => {
            const res = await axios.get(`${API_URL}/api/customer/invitations/${invitation.invitation_id}/waiver-forms`, { withCredentials: true });
            return res.data;
          }}
        />

        {/* Recipients table */}
        <div className="mt-6 bg-white rounded-2xl border border-[#E8E5DF] overflow-hidden">
          <div className="px-4 py-3 border-b border-[#E8E5DF] flex items-center justify-between">
            <h2 className="font-semibold text-[#1E1E24]">Recipients</h2>
            <span className="text-xs text-[#6B6B7A]">{pending} pending response</span>
          </div>
          {recipients.length === 0 ? (
            <p className="p-6 text-center text-sm text-[#6B6B7A]">No recipients yet.</p>
          ) : (
            <div className="divide-y divide-[#E8E5DF]" data-testid="recipients-list">
              {recipients.map((r) => (
                <div key={r.recipient_id} className="px-4 py-3 flex flex-wrap items-center gap-3" data-testid={`recipient-${r.recipient_id}`}>
                  <div className="flex-1 min-w-[160px]">
                    <p className="font-medium text-[#1E1E24] text-sm">{r.name}</p>
                    <p className="text-xs text-[#6B6B7A]">{r.email || r.phone || '—'}</p>
                    {r.rsvp_message && <p className="text-xs italic text-[#6366F1] mt-1">"{r.rsvp_message}"</p>}
                  </div>
                  <StatusPill status={r.status} />
                  <button
                    onClick={() => copyLink(r.token)}
                    className="text-xs text-[#6B6B7A] hover:text-[#FF3B6B] inline-flex items-center gap-1"
                    title="Copy RSVP link"
                    data-testid={`copy-link-${r.recipient_id}`}
                  >
                    {copiedToken === r.token ? <Check className="w-3.5 h-3.5 text-[#06D6A0]" /> : <Copy className="w-3.5 h-3.5" />}
                    Link
                  </button>
                  <button
                    onClick={() => removeRecipient(r.recipient_id)}
                    className="p-1.5 rounded-full hover:bg-[#FFE4EB]"
                    title="Remove"
                    data-testid={`remove-recipient-${r.recipient_id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5 text-[#FF3B6B]" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {invitation.message && (
          <div className="mt-6 bg-white rounded-2xl border border-[#E8E5DF] p-5">
            <p className="text-xs text-[#6B6B7A] mb-1">Personal message</p>
            <p className="text-sm text-[#1E1E24] whitespace-pre-wrap">"{invitation.message}"</p>
          </div>
        )}
      </div>

      {/* Edit Invitation Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="bg-white max-w-lg" data-testid="edit-invite-dialog">
          <DialogHeader>
            <DialogTitle>Edit invitation</DialogTitle>
            <DialogDescription>
              Existing recipients won't get a duplicate. New recipients added later will see the latest details.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-[#6B6B7A]">Child's name</label>
                <Input value={editForm.child_name} onChange={(e) => setEditForm({ ...editForm, child_name: e.target.value })} className="mt-1" data-testid="edit-child-name" />
              </div>
              <div>
                <label className="text-xs text-[#6B6B7A]">Age</label>
                <Input type="number" min="1" max="25" value={editForm.age} onChange={(e) => setEditForm({ ...editForm, age: e.target.value })} className="mt-1" data-testid="edit-age" />
              </div>
              <div>
                <label className="text-xs text-[#6B6B7A]">Date</label>
                <Input type="date" value={editForm.party_date} onChange={(e) => setEditForm({ ...editForm, party_date: e.target.value })} className="mt-1" data-testid="edit-date" />
              </div>
              <div>
                <label className="text-xs text-[#6B6B7A]">Time</label>
                <Input value={editForm.party_time} onChange={(e) => setEditForm({ ...editForm, party_time: e.target.value })} placeholder="e.g. 10:00 AM" className="mt-1" data-testid="edit-time" />
              </div>
            </div>
            <div>
              <label className="text-xs text-[#6B6B7A]">Location</label>
              <Input value={editForm.location} onChange={(e) => setEditForm({ ...editForm, location: e.target.value })} className="mt-1" data-testid="edit-location" />
            </div>
            <div>
              <label className="text-xs text-[#6B6B7A]">Personal message</label>
              <Textarea value={editForm.message} onChange={(e) => setEditForm({ ...editForm, message: e.target.value })} rows={3} className="mt-1" data-testid="edit-message" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)} className="rounded-full">Cancel</Button>
            <Button onClick={saveEdit} disabled={savingEdit} className="rounded-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white" data-testid="edit-save">
              {savingEdit ? 'Saving…' : 'Save changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Recipients Dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="bg-white max-w-lg" data-testid="add-recipients-dialog">
          <DialogHeader>
            <DialogTitle>Add more recipients</DialogTitle>
            <DialogDescription>
              {invitation.status === 'sent'
                ? "We'll send the invite immediately to the new people you add — existing guests won't get duplicates."
                : "Add more guests; you can send the invitation when you're ready."}
            </DialogDescription>
          </DialogHeader>
          <div>
            <label className="text-xs text-[#6B6B7A]">One per line · <em>Name, email, phone</em> (phone optional)</label>
            <Textarea
              value={addText}
              onChange={(e) => setAddText(e.target.value)}
              rows={6}
              placeholder="Alex, alex@email.com, +14165551234&#10;Jamie, jamie@email.com"
              className="mt-1 font-mono text-sm"
              data-testid="add-recipients-textarea"
            />
            <p className="text-[11px] text-[#6B6B7A] mt-1">
              {parseAddText(addText).length} valid recipient{parseAddText(addText).length === 1 ? '' : 's'} parsed
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)} className="rounded-full">Cancel</Button>
            <Button onClick={submitAddRecipients} disabled={addingRecipients} className="rounded-full bg-[#6366F1] hover:bg-[#4F46E5] text-white" data-testid="add-recipients-submit">
              <Send className="w-4 h-4 mr-1.5" />
              {addingRecipients ? 'Sending…' : (invitation.status === 'sent' ? 'Add & send' : 'Add')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InviteDetail;
